import { scrollVerticalAlignment } from '../types/ScrollOnEvent.types';

/**
 * Enum for scroll directions.
 * @readonly
 * @enum {String}
 */
const scrollDirections = {
    UP: 'up',
    DOWN: 'down',
    LEFT: 'left',
    RIGHT: 'right'
};

/**
 * Checks if provided direction is vertical
 * @param {scrollDirections} direction
 * @return {Boolean} - is vertical direction or not
 */
const isVerticalDirection = (direction) => [scrollDirections.UP, scrollDirections.DOWN].includes(direction);

/**
 * Fallback for smooth scroll behavior
 * for safari and old browsers
 * @param {HTMLElement|Window} $scrollEl - scroll container
 * @param {Number} distance - scroll distance
 * @param {String} scrollInitAttr - API container's attr name for initial scroll value
 * @param {Function=} scrollFn - Container's scroll fn
 */
const smoothFallback = ($scrollEl, distance, scrollInitAttr, scrollFn = false) => {
    const animationFrames = 60,
        animationTime = 250,
        intervalTime = Math.floor(animationTime / animationFrames);

    const scrollIndicator = Math.floor( Math.abs($scrollEl[scrollInitAttr] - distance) / animationFrames),
        scrollDiference = Math.abs($scrollEl[scrollInitAttr] - distance) - (scrollIndicator * animationFrames),
        scrollDirection = distance - $scrollEl[scrollInitAttr] > 0 ? 1 : -1;

    let currentFrame = 0, interval;
    scrollFn = scrollFn || ((value) => $scrollEl[scrollInitAttr] = value);

    interval = setInterval(function () {
        var scrollBasis = $scrollEl[scrollInitAttr];
        if (currentFrame < animationFrames) {
            scrollFn(scrollBasis + scrollIndicator * scrollDirection)
        } else {
            scrollFn(scrollBasis + scrollDiference * scrollDirection)
            clearInterval(interval);
        }
        currentFrame++;
    }, intervalTime);
}

/**
 * Workaround for element's scrollTo
 * method with smooth behavior
 * (only for one directional scrolling)
 * @param {HTMLElement} $scrollEl - scroll container
 * @param {Number} distance - scroll distance
 * @param {scrollDirections} direction - scroll direction
 */
const oneDirectionScrollToSmoothFallback = ($scrollEl, distance, direction = scrollDirections.LEFT) => {
    const scrollInitAttr = `scroll${isVerticalDirection(direction) ? 'Top' : 'Left'}`;
    smoothFallback($scrollEl, distance, scrollInitAttr);
}

/**
 * Workaround for element's scrollIntoView
 * method with smooth behavior (only for vertical scrolling)
 * @param {HTMLElement} $targetEl - element to scroll
 * @param {Object} scrollParams - scrollIntoViewOptions (vertical aligment)
 */
const verticalScrollIntoViewSmoothFallback = ($targetEl, { block = scrollVerticalAlignment.start }) => {
    const { offsetTop, offsetHeight } = $targetEl;
    const targetDistances = {
        [scrollVerticalAlignment.START]: offsetTop,
        [scrollVerticalAlignment.CENTER]: Math.floor(offsetTop - (offsetHeight/2)),
        [scrollVerticalAlignment.END]: offsetTop + offsetHeight
    };

    const distance = targetDistances[block] || targetDistances[scrollVerticalAlignment.START];
    const scrollFn = (value) => window.scrollTo($targetEl.scrollX, value);
    smoothFallback(window, distance, 'scrollY', scrollFn);
}

export {
    scrollDirections,
    oneDirectionScrollToSmoothFallback,
    verticalScrollIntoViewSmoothFallback
}